<template>
  <DialogFormStepper
    v-if="dialogAuth"
    v-model="dialogAuth"
    :btn-close="!user || userHasCompletedSetup"
    :factory-id="dialogFactoryId"
    persistent
    :steps="formStepsAuth"
  />

  <suspense>
    <div :class="appClass">
      <router-view />
    </div>
  </suspense>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useMeta } from 'quasar'
import { computed, defineAsyncComponent, nextTick, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Favicon from '@/assets/favicon.ico'
import { useNotifications } from '@/composables/useNotifications'
import { useSsrSafeScreen } from '@/composables/useSsrSafeScreen'
import configApp from '@/config/app.json'
import themeConfig from '@/config/theme.json'
import { useStepsAuth } from '@/factories/formStepsFactory/auth'
import { useAppStore } from '@/stores/modules/app'
import { useAuthStore } from '@/stores/modules/auth'

const DialogFormStepper = defineAsyncComponent(
  () => import('@/components/Dialog/DialogFormStepper.vue'),
)

const router = useRouter()

const {
  dialogAuth,
  dialogFactoryId,
  routeRedirect,
  user,
  userHasCompletedSetup,
} = storeToRefs(useAuthStore())

const { configTheme } = storeToRefs(useAppStore())

const { getFormStepsAuth } = useStepsAuth()
const formStepsAuth = getFormStepsAuth()

const { fontFamilyCdnUrl } = themeConfig.typography

const route = useRoute()

const { notifyDeleteUser, notifySsoError } = useNotifications()

const appClass = computed(() => [
  ...(route?.name?.toString() ? [`page--${route?.name?.toString()}`] : []),
])

const resolveTitle = (v?: string) =>
  v && v.length ? v : configApp.company.name

const screen = useSsrSafeScreen()

const metaDevice = computed<'desktop' | 'mobile'>(() =>
  screen.lt.md ? 'mobile' : 'desktop',
)

const metaScripts = computed(() => {
  const scripts = configTheme.value?.app.meta.script

  if (!scripts) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(scripts)
      .filter(
        ([, script]) => !script.device || script.device === metaDevice.value,
      )
      .map(([key, script]) => {
        const { device: _device, ...attrs } = script

        return [key, attrs]
      }),
  )
})

const metaData = computed(() => {
  const meta = {
    link: {
      dnsPrefetchGstatic: {
        href: 'https://www.gstatic.com',
        rel: 'dns-prefetch',
      },
      dnsPrefetchMapsApi: {
        href: 'https://maps.googleapis.com',
        rel: 'dns-prefetch',
      },
      favicon: {
        href: Favicon,
        rel: 'icon',
        type: 'image/ico',
      },
      preconnectApi: {
        href: process.env.API_BASE_URL,
        rel: 'preconnect',
      },
      preconnectGoogleTagManager: {
        href: 'https://www.googletagmanager.com',
        rel: 'preconnect',
      },
      preconnectGoogleapis: {
        href: 'https://fonts.googleapis.com',
        rel: 'preconnect',
      },
    },
    meta: {
      ogSiteName: { content: configApp.company.name, property: 'og:site_name' },
      ogTitle: {
        template: (title: string) => resolveTitle(title),
      },
      twitterTitle: {
        template: (title: string) => resolveTitle(title),
      },
    },
    ...(Object.keys(metaScripts.value).length
      ? {
          script: metaScripts.value as Record<string, Record<string, string>>,
        }
      : {}),
    titleTemplate: (title: string) => resolveTitle(title),
  }

  if (fontFamilyCdnUrl) {
    Object.assign(meta.link, {
      fontFamilyCdnUrl: {
        href: fontFamilyCdnUrl,
        media: 'print',
        onload: "this.media='all'",
        rel: 'stylesheet',
      },
    })
  }

  return meta
})

useMeta(() => metaData.value)

onMounted(() => {
  notifyDeleteUser()
  notifySsoError()
})

watch(dialogAuth, (v) => {
  if (!v) {
    nextTick(() => {
      dialogFactoryId.value = 'auth'
    })

    if (user.value && routeRedirect.value) {
      router.push(routeRedirect.value)
    }

    routeRedirect.value = undefined
  }
})
</script>

<style lang="scss">
@import '@/css/index';
@import '@/css/transitions';
</style>
